import { Skeleton, Switch } from "antd";
import { openNotification } from "utils/notification";
import {
  NotificationPreferences,
  useGetNotificationPreferences,
  useUpdateNotificationPreferences,
} from "../hooks/useNotificationPreferences";

type ToggleKey = keyof Omit<NotificationPreferences, "timezone">;

const TOGGLES: { key: ToggleKey; title: string; description: string }[] = [
  {
    key: "interview_reminder_email",
    title: "Interview reminders",
    description:
      "An email the day before your scheduled interviews, so you can prepare, confirm the panel, or reschedule.",
  },
  {
    key: "job_expiry_reminder_email",
    title: "Job expiry reminders",
    description:
      "An email 2 days before your active jobs expire, so you can extend, renew, or close them.",
  },
];

export const NotificationSettings = () => {
  const { data, isLoading, isError } = useGetNotificationPreferences();
  const { mutate, isLoading: isSaving, variables } =
    useUpdateNotificationPreferences();

  const handleToggle = (key: ToggleKey, checked: boolean) => {
    mutate(
      { [key]: checked },
      {
        onSuccess: () => {
          openNotification({
            title: "Saved",
            state: "success",
            description: "Notification settings updated",
            duration: 3,
          });
        },
        onError: () => {
          openNotification({
            title: "Error",
            state: "error",
            description: "Could not update notification settings. Please try again.",
            duration: 4.5,
          });
        },
      }
    );
  };

  return (
    <div>
      <div className="mb-4">
        <h1 className="font-semibold text-lg text-accent">Email notifications</h1>
        <h3 className="text-sm">Choose which priority action emails you receive.</h3>
      </div>
      <hr />

      {isLoading ? (
        <Skeleton active className="mt-6" />
      ) : isError || !data ? (
        <p className="mt-6 text-sm text-red-500">
          Could not load your notification settings. Please refresh the page.
        </p>
      ) : (
        <>
          {TOGGLES.map(({ key, title, description }) => (
            <div
              key={key}
              className="flex items-start justify-between gap-x-6 py-5 border-b"
            >
              <div>
                <h4 className="font-medium" id={`${key}-label`}>
                  {title}
                </h4>
                <p className="text-sm text-gray-500">{description}</p>
              </div>
              <Switch
                aria-labelledby={`${key}-label`}
                checked={data[key]}
                loading={isSaving && variables?.[key] !== undefined}
                onChange={(checked) => handleToggle(key, checked)}
              />
            </div>
          ))}
          <p className="text-sm text-gray-500 mt-4">
            Reminders are sent in your time zone: <strong>{data.timezone}</strong>.
            It's detected from your browser when you sign in.
          </p>
        </>
      )}
    </div>
  );
};
